// ─── Collage Element: Text Note ───────────────────────────────
// Handwritten note on paper. Optional torn-edge clip-path.
// Applies customStyle overrides (font, size, color, align).

import React from 'react';
import { motion } from 'framer-motion';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';
import { useCollageElementMotion } from '../../../lib/utils/collageMotion';

export default function TextNote({ element, isMobile, theme, skipPositioning, isEditing, onEditText }) {
  const style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const rotation = `${element.rotation || 0}deg`;
  const motionProps = useCollageElementMotion(element);
  const torn = element.style === 'torn-note';
  const cs = element.customStyle || {};

  const handleBlur = (e) => {
    if (onEditText) onEditText('content', e.currentTarget.innerText);
  };

  return (
    <motion.div
      {...motionProps}
      style={{ ...style, rotate: rotation }}
      className={isEditing ? 'select-text' : 'select-none'}
    >
      <div
        className={`${torn ? 'rounded-[14px]' : 'rounded-[10px]'} px-5 py-4 sm:px-6 sm:py-5 relative overflow-hidden`}
        style={{
          background: cs.backgroundColor || theme.paper || theme.surface,
          color: cs.color || theme.textPrimary,
          fontFamily: cs.fontFamily || theme.handFont || 'cursive',
          boxShadow: cs.backgroundColor === 'transparent' ? 'none' : (theme.shadowPaper || '0 10px 30px rgba(0,0,0,0.08)'),
          textAlign: cs.textAlign || undefined,
          clipPath: torn
            ? 'polygon(2% 5%, 98% 0%, 100% 95%, 93% 100%, 4% 97%, 0% 10%)'
            : undefined,
        }}
      >
        {/* Paper grain texture overlay */}
        {cs.backgroundColor !== 'transparent' && (
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.35]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
              backgroundSize: '128px 128px',
              mixBlendMode: 'multiply',
            }}
          />
        )}
        {isEditing ? (
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={handleBlur}
            onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
            className={`outline-none ring-1 ring-blue-400/30 rounded ${!cs.fontSize ? 'text-[18px] leading-[1.25] sm:text-[24px]' : ''}`}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              fontSize: cs.fontSize || undefined,
              lineHeight: cs.fontSize ? 1.3 : undefined,
              minHeight: '1em',
            }}
            data-testid={`inline-edit-${element.id}`}
          >
            {element.content}
          </div>
        ) : (
          <div
            className={!cs.fontSize ? 'text-[18px] leading-[1.25] sm:text-[24px]' : undefined}
            style={{
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              fontSize: cs.fontSize || undefined,
              lineHeight: cs.fontSize ? 1.3 : undefined,
            }}
          >
            {element.content}
          </div>
        )}
      </div>
    </motion.div>
  );
}
